import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import NotificationToggle from './components/NotificationToggle';
import QuietHoursConfig from './components/QuietHoursConfig';
import NotificationPreview from './components/NotificationPreview';
import { trackNotificationSettingsChanged } from '../../utils/analytics';
import { validateQuietHoursRange, validateFieldType } from '../../utils/validation';
import { trackValidationError, trackSubmissionError } from '../../utils/errorTracking';

const NotificationSettings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [settings, setSettings] = useState({
    inAppNotifications: true,
    emailNotifications: true,
    smsNotifications: false,
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00'
  });

  useEffect(() => {
    if (!user) {
      navigate('/signin');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('user_profiles')?.select('in_app_notifications, email_notifications, sms_notifications, quiet_hours_enabled, quiet_hours_start, quiet_hours_end')?.eq('id', user?.id)?.single();

      if (error) throw error;

      if (data) {
        setSettings({
          inAppNotifications: data?.in_app_notifications ?? true,
          emailNotifications: data?.email_notifications ?? true,
          smsNotifications: data?.sms_notifications ?? false,
          quietHoursEnabled: data?.quiet_hours_enabled ?? false,
          quietHoursStart: data?.quiet_hours_start || '22:00',
          quietHoursEnd: data?.quiet_hours_end || '08:00'
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleTimeChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    
    // Clear validation error for this field
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors?.[key];
      return newErrors;
    });
  };

  const validateSettings = () => {
    const errors = {};

    // Validate field types
    const inAppValidation = validateFieldType(settings?.inAppNotifications, 'boolean', 'In-App Notifications');
    if (!inAppValidation?.valid) {
      errors.inAppNotifications = inAppValidation?.error;
      trackValidationError('inAppNotifications', inAppValidation?.error, 'notification-settings');
    }

    const emailValidation = validateFieldType(settings?.emailNotifications, 'boolean', 'Email Notifications');
    if (!emailValidation?.valid) {
      errors.emailNotifications = emailValidation?.error;
      trackValidationError('emailNotifications', emailValidation?.error, 'notification-settings');
    }

    const smsValidation = validateFieldType(settings?.smsNotifications, 'boolean', 'SMS Notifications');
    if (!smsValidation?.valid) {
      errors.smsNotifications = smsValidation?.error;
      trackValidationError('smsNotifications', smsValidation?.error, 'notification-settings');
    }

    const quietEnabledValidation = validateFieldType(settings?.quietHoursEnabled, 'boolean', 'Quiet Hours Enabled');
    if (!quietEnabledValidation?.valid) {
      errors.quietHoursEnabled = quietEnabledValidation?.error;
      trackValidationError('quietHoursEnabled', quietEnabledValidation?.error, 'notification-settings');
    }

    // Validate quiet hours times if enabled
    if (settings?.quietHoursEnabled) {
      const rangeValidation = validateQuietHoursRange(settings?.quietHoursStart, settings?.quietHoursEnd);
      if (!rangeValidation?.valid) {
        errors.quietHours = rangeValidation?.error;
        trackValidationError('quietHours', rangeValidation?.error, 'notification-settings');
      }
    }

    return errors;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setValidationErrors({});

      // Validate settings before saving
      const errors = validateSettings();
      if (Object.keys(errors)?.length > 0) {
        setValidationErrors(errors);
        alert('Please fix validation errors before saving.');
        return;
      }

      const { error } = await supabase?.from('user_profiles')?.update({
          in_app_notifications: settings?.inAppNotifications,
          email_notifications: settings?.emailNotifications,
          sms_notifications: settings?.smsNotifications,
          quiet_hours_enabled: settings?.quietHoursEnabled,
          quiet_hours_start: settings?.quietHoursStart,
          quiet_hours_end: settings?.quietHoursEnd
        })?.eq('id', user?.id);

      if (error) {
        throw error;
      }

      // Track notification settings change
      trackNotificationSettingsChanged('all', settings);

      // Show success feedback
      alert('Notification settings saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      const errorMessage = error?.message || 'Failed to save settings. Please try again.';
      trackSubmissionError('save_notification_settings_error', errorMessage, { userId: user?.id });
      alert(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <Icon name="ArrowLeft" size={20} />
            </Button>
            <h1 className="font-heading text-3xl font-bold text-foreground">
              Notification Settings
            </h1>
          </div>
          <p className="text-muted-foreground ml-14">
            Manage how you receive updates about your meetings and activities
          </p>
        </div>

        <div className="space-y-6">
          {/* Validation Errors */}
          {Object.keys(validationErrors)?.length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-destructive mb-2">Validation Errors</h3>
                  <ul className="text-sm text-destructive/90 space-y-1">
                    {Object.entries(validationErrors)?.map(([field, error]) => (
                      <li key={field}>• {error}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Notification Types Section */}
          <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Bell" size={20} className="text-primary" />
              <h2 className="font-heading text-xl font-semibold text-foreground">
                Notification Types
              </h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Choose how you want to be notified about meeting updates
            </p>

            <div className="space-y-4">
              <NotificationToggle
                icon="Monitor"
                label="In-App Alerts"
                description="Show notifications within the application when you're actively using it"
                checked={settings?.inAppNotifications}
                onChange={() => handleToggle('inAppNotifications')}
              />
              <NotificationToggle
                icon="Mail"
                label="Email Notifications"
                description="Receive meeting updates and confirmations via email"
                checked={settings?.emailNotifications}
                onChange={() => handleToggle('emailNotifications')}
              />
              <NotificationToggle
                icon="MessageSquare"
                label="SMS Notifications"
                description="Get text messages for urgent meeting updates and reminders"
                checked={settings?.smsNotifications}
                onChange={() => handleToggle('smsNotifications')}
              />
            </div>
          </div>

          {/* Quiet Hours Section */}
          <QuietHoursConfig
            enabled={settings?.quietHoursEnabled}
            startTime={settings?.quietHoursStart}
            endTime={settings?.quietHoursEnd}
            onToggle={() => handleToggle('quietHoursEnabled')}
            onStartTimeChange={(value) => handleTimeChange('quietHoursStart', value)}
            onEndTimeChange={(value) => handleTimeChange('quietHoursEnd', value)}
          />

          {/* Notification Preview Section */}
          <NotificationPreview settings={settings} />

          {/* Save Button */}
          <div className="flex items-center justify-end gap-3 pt-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              loading={saving}
              iconName="Save"
              iconPosition="left"
            >
              Save Settings
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;